import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useIntl, getLocale } from 'umi';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { IDomEditor, IEditorConfig, IToolbarConfig, i18nChangeLanguage } from '@wangeditor/editor';
import { Modal, Button } from 'tea-component';
import { LANGUAGE_EN } from '@home/_config/config';
import '@wangeditor/editor/dist/css/style.css';
import TagInput from '../TagInput';
import { hasBulletedListTool } from '../../util';
import classNames from 'classnames';
import './index.scss';

interface IEditNotesModalProps {
  visible: boolean;
  isEn: boolean;
  loading?: boolean;
  defaultTags: string[];
  defaultContent: string;
  originalTags: string[];
  originalContent: string;
  onConfirm: (tags: string[], content: string) => void;
  onCancel: () => void;
  extraToolList?: string[];
}

const EditNotesModal: React.FC<IEditNotesModalProps> = ({
  visible,
  isEn,
  loading = false,
  defaultTags,
  defaultContent,
  originalTags,
  originalContent,
  onConfirm,
  onCancel,
  extraToolList = [],
}) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEN = locale === LANGUAGE_EN;

  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const [html, setHtml] = useState<string>(defaultContent || '');
  const [tags, setTags] = useState<string[]>(defaultTags || []);

  const initialEditorHtmlRef = useRef<string>('');
  const enableBulletedList = hasBulletedListTool(extraToolList);

  const toolbarConfig: Partial<IToolbarConfig> = {
    toolbarKeys: [
      'bold',
      'italic',
      'undo',
      'redo',
      ...(enableBulletedList ? extraToolList : []),
    ],
    excludeKeys: [],
  };

  const editorConfig: Partial<IEditorConfig> = {
    placeholder: isEn ? 'Enter notes...' : '请输入笔记...',
    customPaste: (_editor: IDomEditor, e: ClipboardEvent) => {
      const { clipboardData } = e;
      if (!clipboardData) return true;
      return ![...clipboardData.items, ...clipboardData.files].some(
        item => item.type.startsWith('image/'),
      );
    },
  };

  useEffect(() => {
    i18nChangeLanguage(isEN ? 'en' : 'zh-CN');
  }, [isEN]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
        setEditor(null);
      }
    };
  }, [editor]);

  useEffect(() => {
    setHtml(defaultContent || '');
    setTags(defaultTags || []);
    initialEditorHtmlRef.current = '';
  }, [defaultContent, defaultTags]);

  const handleEditorChange = (editorInstance: IDomEditor): void => {
    const text = editorInstance.getText();
    let resultVal = editorInstance.getHtml();
    if (text.trim() === '') {
      resultVal = '';
    }
    if (!initialEditorHtmlRef.current) {
      initialEditorHtmlRef.current = resultVal;
    }
    setHtml(resultVal);
  };

  const hasChanges = useMemo(() => {
    const origTags = originalTags || [];
    const tagsChanged =
      tags.length !== origTags.length ||
      tags.some((t, i) => t !== origTags[i]);

    const wasAlreadyEdited = (defaultContent || '') !== (originalContent || '');
    const userModified = initialEditorHtmlRef.current
      ? html !== initialEditorHtmlRef.current
      : false;

    return tagsChanged || wasAlreadyEdited || userModified;
  }, [tags, html, originalTags, originalContent, defaultContent]);

  const handleSubmit = (): void => {
    onConfirm(tags, html);
  };

  const handleCancel = (): void => {
    setHtml(defaultContent || '');
    setTags(defaultTags || []);
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      size={1000}
      caption={isEn ? 'Edit Notes' : '编辑笔记'}
      onClose={handleCancel}
    >
      <Modal.Body>
        <div className="dbh-edit-notes-modal">
          <TagInput
            label={isEn ? 'Tag' : '标签'}
            tags={tags}
            onChange={setTags}
            placeholder={isEn ? 'Enter tag and press Enter' : '输入标签后按回车'}
          />
          <div className="dbh-edit-notes-modal__editor">
            <span className="dbh-edit-notes-modal__editor-label">
              {isEn ? 'Notes' : '笔记'}
            </span>
            <div
              className={classNames('dbh-edit-notes-modal__editor-wrapper', {
                'dbh-edit-notes-modal__editor-wrapper--bulleted-list':
                  enableBulletedList,
              })}
            >
              <Toolbar
                editor={editor}
                defaultConfig={toolbarConfig}
                mode="simple"
                style={{ borderBottom: '1px solid #d0d3d9' }}
              />
              <Editor
                defaultConfig={editorConfig}
                value={html}
                onCreated={setEditor}
                onChange={handleEditorChange}
                mode="simple"
                style={{ height: '400px', overflowY: 'hidden', width: '100%' }}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" onClick={handleSubmit} loading={loading} disabled={!hasChanges}>
          {formatMessage({ id: 'dbh_studio_funcom_edit_modal_comfirm' })}
        </Button>
        <Button type="weak" onClick={handleCancel}>
          {formatMessage({ id: 'dbh_studio_funcom_edit_modal_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditNotesModal;
